import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '../common/Card';
import { Badge, BadgeTone } from '../common/Badge';
import { FREEMIUM_INVOICE_LIMIT, FREEMIUM_SOFT_WARNING_THRESHOLD } from '../../config/freemium';
import { buildUpgradeHref } from '../../config/nudgePromotions';
import analyticsService from '../../services/analyticsService';

/**
 * `components/usage/InvoiceUsageMeter` (M-UX-G4, modules-ux-restructure.md).
 *
 * Zeigt Free-Tier-Nutzern VOR dem Limit-Moment einen ruhigen Zähler
 * "N von {{limit}} Rechnungen diesen Monat" — Nordstern: Upgrade wird eine
 * geplante Entscheidung statt einer Frust-Notbremse beim 403-Limit-Treffer
 * (`usageLimitPromo`-Banner in InvoiceDashboard/XRechnungDashboard/
 * MobileInvoiceDashboard, unverändert von diesem Modul).
 *
 * ISOLIERT PRÜFBAR (kein Context-Mocking nötig): die Komponente liest
 * `sellerData`/`isPremium` NICHT selbst aus `useAuth()` — der Aufrufer
 * übergibt `count`/`isPremium` explizit als Props (Golden-Rules "Pure
 * functions bevorzugen — macht Tests trivial").
 *
 * @module components/usage/InvoiceUsageMeter
 */
export interface InvoiceUsageMeterProps {
  /**
   * `sellerData?.monthlyInvoiceCount ?? 0` — PHP-Backend-verwaltet,
   * READ-ONLY (siehe `types/invoiceTypes.ts#Seller.monthlyInvoiceCount`).
   * Negative/NaN-Werte werden defensiv auf `0` geklemmt (Fail-closed —
   * ein kaputter Zählerstand darf nie eine falsche "fast voll"-Warnung
   * unterdrücken oder eine Negativ-Anzeige erzeugen).
   */
  count: number;
  /** `useAuth().isPremium` — Premium-Nutzer sehen KEINEN Zähler (Komponente rendert `null`). */
  isPremium: boolean;
  /** Anzeige-Limit. Default {@link FREEMIUM_INVOICE_LIMIT}. Als Prop für Testbarkeit ohne Config-Mock. */
  limit?: number;
  className?: string;
}

/**
 * Rechnungs-Verbrauchszähler für Free-Tier-Nutzer. Rendert `null` für
 * Premium-Nutzer (`isPremium === true`) — kein Zähler, kein Layout-Rest.
 *
 * Schwellen (fest, s. `config/freemium.ts`):
 * - `count < FREEMIUM_SOFT_WARNING_THRESHOLD` (0–2 von 4): Ton `neutral`, kein Zusatzhinweis.
 * - `count >= FREEMIUM_SOFT_WARNING_THRESHOLD` und `< limit` (3 von 4): Ton `caution`, sanfter Hinweis + Upgrade-Link.
 * - `count >= limit` (4+ von 4): Ton `warning` ("Limit erreicht") + `limitHint`-Zeile
 *   + Upgrade-Link — der eigentliche 403-Moment zeigt zusätzlich den separaten
 *   `usageLimitPromo`-Banner mit Value-Framing; dieser Meter bleibt hier bewusst
 *   knapp (keine Dopplung der Botschaft).
 *
 * Telemetrie (M-N2, architecture-nudge.md §7.4 — Befund N-1 gefixt: der
 * soft-Zustand hatte trotz Docstring-Versprechen keinen Link, der at-Limit-
 * Zustand gar keinen Hinweis): `view_promotion` feuert 1× pro
 * Zustands-Flanke (Mount + Wechsel zwischen neutral/soft/limit — kein
 * Session-Dedup, Sichtkontakt-Frequenz ist selbst Information), `isPremium`
 * unterdrückt jedes Event. `select_promotion` feuert beim Klick auf den
 * jeweiligen Upgrade-Link. Der Hook wird UNBEDINGT aufgerufen (vor dem
 * `isPremium`-Early-Return) — `isPremium` ist ein veränderliches Prop, ein
 * bedingter Hook-Aufruf davor wäre ein Rules-of-Hooks-Verstoß, sobald sich
 * der Premium-Status während der Komponenten-Lebenszeit ändert.
 */
export function InvoiceUsageMeter({
  count,
  isPremium,
  limit = FREEMIUM_INVOICE_LIMIT,
  className = '',
}: InvoiceUsageMeterProps) {
  const { t } = useTranslation('common');

  const safeCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  const safeLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : FREEMIUM_INVOICE_LIMIT;
  const clampedCount = Math.min(safeCount, safeLimit);
  const isAtLimit = safeCount >= safeLimit;
  const isNearLimit = !isAtLimit && safeCount >= FREEMIUM_SOFT_WARNING_THRESHOLD;

  // Hook läuft UNBEDINGT (vor dem isPremium-Early-Return, s. Docstring) —
  // die Zustands-Flanke selbst (isAtLimit/isNearLimit-Wechsel) ist die
  // Dedup-Grenze, isPremium wird innerhalb des Effekts geprüft.
  useEffect(() => {
    if (isPremium) return;
    if (isAtLimit) {
      analyticsService.trackViewPromotion({
        promotion_id: 'usage_meter_limit',
        limit_value: safeLimit,
        count_value: safeCount,
      });
    } else if (isNearLimit) {
      analyticsService.trackViewPromotion({
        promotion_id: 'usage_meter_soft',
        limit_value: safeLimit,
        count_value: safeCount,
      });
    }
  }, [isPremium, isAtLimit, isNearLimit, safeLimit, safeCount]);

  if (isPremium) return null;

  const handleUpgradeLinkClick = (promotionId: 'usage_meter_soft' | 'usage_meter_limit') => {
    analyticsService.trackSelectPromotion({
      promotion_id: promotionId,
      limit_value: safeLimit,
      count_value: safeCount,
    });
  };

  const progressPct = safeLimit > 0 ? Math.min(100, (clampedCount / safeLimit) * 100) : 0;

  const tone: BadgeTone = isAtLimit ? 'warning' : isNearLimit ? 'caution' : 'neutral';
  const badgeLabel = isAtLimit
    ? t('usageMeter.badgeAtLimit')
    : isNearLimit
    ? t('usageMeter.badgeNearLimit')
    : t('usageMeter.badgeOk');
  const barColorClass = isAtLimit ? 'bg-amber-500' : isNearLimit ? 'bg-yellow-400' : 'bg-indigo-500';

  return (
    <Card
      className={`mb-4 ${className}`.trim()}
      role="status"
      aria-label={t('usageMeter.ariaLabel', { count: safeCount, limit: safeLimit })}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-primary">
            {t('usageMeter.label', { count: safeCount, limit: safeLimit })}
          </span>
          <Badge tone={tone}>{badgeLabel}</Badge>
        </div>
        <div className="w-full sm:w-40 h-2 rounded-full bg-surface-inset overflow-hidden" aria-hidden="true">
          <div
            className={`h-full rounded-full transition-all ${barColorClass}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
      {isNearLimit && (
        <p className="mt-2 text-xs text-secondary">
          {t('usageMeter.softHint')}{' '}
          <Link
            to={buildUpgradeHref('usage_meter_soft')}
            onClick={() => handleUpgradeLinkClick('usage_meter_soft')}
            className="font-medium text-accent-ink hover:text-indigo-300 underline"
          >
            {t('usageMeter.upgradeLinkSoft')}
          </Link>
        </p>
      )}
      {isAtLimit && (
        <p className="mt-2 text-xs text-secondary">
          {t('usageMeter.limitHint')}{' '}
          <Link
            to={buildUpgradeHref('usage_meter_limit')}
            onClick={() => handleUpgradeLinkClick('usage_meter_limit')}
            className="font-medium text-accent-ink hover:text-indigo-300 underline"
          >
            {t('usageMeter.upgradeLinkLimit')}
          </Link>
        </p>
      )}
    </Card>
  );
}

export default InvoiceUsageMeter;
